import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import PrivateRoute from './components/PrivateRoute';
import GroupSlugWrapper from './components/GroupSlugWrapper';

// Import Pages
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import HOALogin from './pages/HOALogin';
import PropertyGroupSelection from './pages/PropertyGroupSelection';
import Dashboard from './pages/Dashboard';
import ResidentDashboard from './pages/ResidentDashboard';
import Documents from './pages/Documents';
import Events from './pages/Events';
import Issues from './pages/Issues';
import Payments from './pages/Payments';
import PaymentHistory from './pages/PaymentHistory';
import Vendors from './pages/Vendors';
import Settings from './pages/Settings';
import VendorDashboard from './pages/VendorDashboard';
import VendorShowcase from './pages/VendorShowcase';
import Users from './pages/Users';
import Properties from './pages/Properties';
import PropertyGroups from './pages/PropertyGroups';
import Pricing from './pages/Pricing';
import Signup from './pages/Signup';
import Billing from './pages/Billing';
import SubscriptionSuccess from './pages/SubscriptionSuccess';
import SubscriptionCancelled from './pages/SubscriptionCancelled';
import Subscription from './pages/Subscription';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import CustomerManagement from './pages/CustomerManagement';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import CashFlow from './pages/CashFlow';
import Expenses from './pages/Expenses';
import BulkImport from './pages/BulkImport';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Security from './pages/Security';
import Profile from './pages/Profile';

// Role-based Dashboard — redirects admin to slug-prefixed URL
const RoleBasedDashboard: React.FC = () => {
  const { user } = useAuth();
  const { groupSlug } = useParams<{ groupSlug?: string }>();

  if (user?.role === 'RESIDENT') {
    return <ResidentDashboard />;
  } else if (user?.role === 'VENDOR') {
    return <Navigate to="/vendor-dashboard" replace />;
  } else if (user?.role === 'ADMIN') {
    // If we're already inside a slug route, render the dashboard
    if (groupSlug) {
      return <Dashboard />;
    }
    // Otherwise redirect to slug-based URL if slug is known
    const slug = localStorage.getItem('selectedPropertyGroupSlug');
    const id = localStorage.getItem('selectedPropertyGroup');
    if (slug) {
      return <Navigate to={`/${slug}/dashboard`} replace />;
    }
    if (!id) {
      return <Navigate to="/select-group" replace />;
    }
    // Has group ID but no slug yet — send to select-group to re-pick (stores slug)
    return <Navigate to="/select-group" replace />;
  } else {
    return <Dashboard />;
  }
};

// Redirect /dashboard → /:slug/dashboard for admins that already have a group selected
const AdminDashboardRedirect: React.FC = () => {
  const { user } = useAuth();
  if (user?.role === 'ADMIN') {
    const slug = localStorage.getItem('selectedPropertyGroupSlug');
    if (slug) return <Navigate to={`/${slug}/dashboard`} replace />;
    return <Navigate to="/select-group" replace />;
  }
  return (
    <Layout>
      <RoleBasedDashboard />
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <div className="min-h-screen bg-background-900 text-white">
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/property/:propertySlug/login" element={<HOALogin />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/security" element={<Security />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/billing" element={<Billing />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/cancelled" element={<SubscriptionCancelled />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<PrivateRoute allowedRoles={['PLATFORM_ADMIN']}><AdminDashboard /></PrivateRoute>} />
            <Route path="/admin/customers" element={<PrivateRoute allowedRoles={['PLATFORM_ADMIN']}><CustomerManagement /></PrivateRoute>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />

            {/* Group selection */}
            <Route path="/select-group" element={<PrivateRoute><PropertyGroupSelection /></PrivateRoute>} />
            <Route path="/select-property-group" element={<Navigate to="/select-group" replace />} />
            <Route path="/select-hoa" element={<Navigate to="/select-group" replace />} />

            {/* Legacy flat routes — residents & vendors still use these; admins get redirected */}
            <Route path="/dashboard" element={<PrivateRoute><AdminDashboardRedirect /></PrivateRoute>} />
            <Route path="/documents" element={<PrivateRoute><Layout><Documents /></Layout></PrivateRoute>} />
            <Route path="/events" element={<PrivateRoute><Layout><Events /></Layout></PrivateRoute>} />
            <Route path="/issues" element={<PrivateRoute><Layout><Issues /></Layout></PrivateRoute>} />
            <Route path="/payments" element={<PrivateRoute><Layout><Payments /></Layout></PrivateRoute>} />
            <Route path="/payments/history/:month" element={<PrivateRoute><Layout><PaymentHistory /></Layout></PrivateRoute>} />
            <Route path="/vendors" element={<PrivateRoute><Layout><Vendors /></Layout></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><Layout><Settings /></Layout></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/subscription" element={<PrivateRoute><Layout><Subscription /></Layout></PrivateRoute>} />
            <Route path="/users" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><Users /></Layout></PrivateRoute>} />
            <Route path="/properties" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><Properties /></Layout></PrivateRoute>} />
            <Route path="/bulk-import" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><BulkImport /></Layout></PrivateRoute>} />
            <Route path="/cash-flow" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><CashFlow /></Layout></PrivateRoute>} />
            <Route path="/expenses" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><Expenses /></Layout></PrivateRoute>} />
            <Route path="/property-groups" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><PropertyGroups /></Layout></PrivateRoute>} />
            <Route path="/vendor-dashboard" element={<PrivateRoute><VendorDashboard /></PrivateRoute>} />
            <Route path="/vendor-showcase" element={<PrivateRoute><Layout><VendorShowcase /></Layout></PrivateRoute>} />

            {/* ── Slug-based admin routes ── */}
            {/* e.g. /olive-garden-townhomes/dashboard */}
            <Route
              path="/:groupSlug"
              element={
                <PrivateRoute>
                  <GroupSlugWrapper />
                </PrivateRoute>
              }
            >
              <Route path="dashboard" element={<Layout><RoleBasedDashboard /></Layout>} />
              <Route path="documents" element={<Layout><Documents /></Layout>} />
              <Route path="events" element={<Layout><Events /></Layout>} />
              <Route path="issues" element={<Layout><Issues /></Layout>} />
              <Route path="payments" element={<Layout><Payments /></Layout>} />
              <Route path="payments/history/:month" element={<Layout><PaymentHistory /></Layout>} />
              <Route path="vendors" element={<Layout><Vendors /></Layout>} />
              <Route path="users" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><Users /></Layout></PrivateRoute>} />
              <Route path="properties" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><Properties /></Layout></PrivateRoute>} />
              <Route path="bulk-import" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><BulkImport /></Layout></PrivateRoute>} />
              <Route path="cash-flow" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><CashFlow /></Layout></PrivateRoute>} />
              <Route path="expenses" element={<PrivateRoute allowedRoles={['ADMIN']}><Layout><Expenses /></Layout></PrivateRoute>} />
              <Route path="settings" element={<Layout><Settings /></Layout>} />
              <Route path="subscription" element={<Layout><Subscription /></Layout>} />
            </Route>
          </Routes>
        </div>
      </AuthProvider>
    </Router>
  );
};

export default App;
